import { useTranslations } from '@i18n/useLanguage'
import { useEffect, useState } from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import { RegionCard } from '@app/components/blocks/card/RegionCard'
import { Style, styled } from 'inlines'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { pluralize } from '@i18n/localizeHelper'
import { SearchTitle } from '@app/components/blocks/searchGrid/SearchTitle'
import useMediaQuery from '@app/util/useMediaQuery'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { ListRegionModel } from 'functions/app/geography/list/region'
import { createRandomGenerator } from '@app/util/helpers'

export const ProvinceSection = ({
  searchTerm,
  onResetSearch,
}: {
  searchTerm: string
  onResetSearch: () => void
}) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.discoveryPage
  const client = useClient()
  const [regions, setRegions] = useState<ListRegionModel[]>([])
  const [mounted, setMounted] = useState(false)
  const [, navigate] = useLocation()

  const dataServerQuery = useQuery('list-region', {
    sortByPartnerFirst: true,
  })
  const serverRegions: ListRegionModel[] = dataServerQuery.data ?? []

  useEffect(() => {
    const close = client
      .query('list-region', {
        searchTerm: searchTerm,
        sortByPartnerFirst: true,
      })
      .subscribe((result) => {
        setRegions(result)
        setMounted(true)
      })
    return () => close()
  }, [client, searchTerm])

  const renderedRegions = mounted ? regions : serverRegions

  const gridStyle: Style = isMobile
    ? {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))',
        gap: '1rem',
      }
    : {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(300px, 1fr))',
        '@media (max-width: 1400px)': {
          gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
        },
        '@media (max-width: 1200px)': {
          gridTemplateColumns: 'repeat(2, minmax(300px, 1fr))',
        },
        gap: '1rem',
      }

  const random = getMinuteRandom()

  return (
    <Column alignItems="stretch" style={{ width: '100%' }}>
      <Title.H3 style={{ alignSelf: 'start' }}>
        <SearchTitle
          title={translations.tabs.regions.provinceTitle}
          searchTerm={searchTerm}
          onClick={onResetSearch}
        />
      </Title.H3>
      <Text.Medium>
        {searchTerm
          ? pluralize(translations.searchResults, renderedRegions.length)
          : translations.tabs.regions.provinceSubTitle}
      </Text.Medium>
      <styled.div style={gridStyle}>
        {renderedRegions.map((region) => (
          <RegionCard
            id={region.id}
            key={region.code}
            title={region.name}
            subsidies={null}
            isCrowdbuildingPartner={region.crowdbuildingPartner}
            initiatives={region.initiatives?.count ?? 0}
            avatars={region.followers ?? []}
            colorIndex={Math.floor(random() * 4)}
            onClick={() => navigate(pages.regionPage(region.slug))}
          />
        ))}
      </styled.div>
    </Column>
  )
}

const getMinuteRandom = () => {
  const now = Date.now()
  const minute = Math.floor(now / (60 * 1000))
  return createRandomGenerator(minute)
}
